import api from './api';
import type {
  PaginatedResponse,
  SiteListItem,
  SiteDetail,
  OrganizationDetail,
  TeamMember,
  InviteItem,
  VendorDirectoryItem,
  VendorConnection,
  PlatformInvite,
  ServiceCoverage,
  PatrolServicePlan,
  RequiredVisit,
  VisitVerification,
  PatrolException,
  PatrolPlanChangeRequest,
} from '../types';

import type { PublishStatus } from '../types';

export interface VendorInfo {
  id: string;
  name: string;
  phone: string | null;
  website: string | null;
  score: number;
  site_count: number;
  publish_status?: PublishStatus | null;
}

export const clientApi = {
  // Sites
  getSites(params?: Record<string, string>) {
    return api.get<PaginatedResponse<SiteListItem>>('/client/sites/', { params });
  },
  getSite(id: string) {
    return api.get<SiteDetail>(`/client/sites/${id}/`);
  },
  createSite(data: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    timezone?: string;
  }) {
    return api.post<SiteDetail>('/client/sites/', data);
  },
  updateSite(id: string, data: Record<string, unknown>) {
    return api.patch<SiteDetail>(`/client/sites/${id}/`, data);
  },
  assignVendor(siteId: string, vendorOrgId: string) {
    return api.post<SiteDetail>(`/client/sites/${siteId}/assign-vendor/`, {
      vendor_org_id: vendorOrgId,
    });
  },
  updateSiteContext(siteId: string, data: Record<string, unknown>) {
    return api.patch(`/client/sites/${siteId}/context/`, data);
  },
  getContextSuggestions(siteId: string) {
    return api.get<Array<{
      id: string;
      suggestion_text: string;
      created_at: string;
      suggested_by_user_email: string | null;
      suggested_by_vendor_name: string | null;
    }>>(`/client/sites/${siteId}/context-suggestions/`).then((r) => r.data);
  },
  decideContextSuggestion(siteId: string, suggestionId: string, action: 'applied' | 'dismissed') {
    return api.post(
      `/client/sites/${siteId}/context-suggestions/${suggestionId}/decide/`,
      { action },
    );
  },

  // Vendors
  getVendors(params?: Record<string, string>) {
    return api.get<PaginatedResponse<VendorInfo>>('/client/vendors/', { params });
  },
  getVendor(id: string) {
    return api.get<VendorInfo>(`/client/vendors/${id}/`);
  },

  // Organization
  getOrganization() {
    return api.get<OrganizationDetail>('/client/organization/');
  },
  updateOrganization(data: Record<string, unknown>) {
    return api.patch<OrganizationDetail>('/client/organization/', data);
  },

  // Team
  getTeam(params?: Record<string, string>) {
    return api.get<PaginatedResponse<TeamMember>>('/client/team/', { params });
  },

  // Invites
  getInvites() {
    return api.get<InviteItem[]>('/client/invites/');
  },
  createInvite(data: { email: string; org_role: 'member' | 'admin' }) {
    return api.post<InviteItem>('/client/invites/', data);
  },

  // Dashboard
  getRecentIssues(limit = 10) {
    return api.get<Array<{
      id: string;
      rule_name: string;
      rule_category: string;
      severity_level: string;
      is_positive: boolean;
      entity_type: string;
      entity_name: string;
      score_impact: number;
      explanation: string;
      created_at: string;
    }>>('/client/recent-issues/', { params: { limit: String(limit) } });
  },

  // Platform Invites (cross-role)
  sendPlatformInvite(email: string) {
    return api.post<PlatformInvite>('/auth/platform-invites/', { email });
  },
  getPlatformInvites() {
    return api.get<PlatformInvite[]>('/auth/platform-invites/');
  },

  // Vendor Directory
  getDirectory(params?: Record<string, string>) {
    return api.get<PaginatedResponse<VendorDirectoryItem>>('/client/directory/', { params });
  },

  // Connections
  getConnections(params?: Record<string, string>) {
    return api.get<PaginatedResponse<VendorConnection>>('/client/connections/', { params });
  },
  requestConnection(vendorOrgId: string) {
    return api.post<VendorConnection>('/client/connections/', { vendor_org_id: vendorOrgId });
  },
  cancelConnection(id: string) {
    return api.delete(`/client/connections/${id}/cancel/`);
  },
  cancelVendor(vendorId: string) {
    return api.post<{ detail: string }>(`/client/vendors/${vendorId}/cancel/`);
  },

  // ---------------- Patrol Obligation Model (Sprint 1) ----------------
  // Sites carry 1..N ServiceCoverages; a mobile_patrol coverage carries
  // a PatrolServicePlan with required_visits.  The client owns the plan
  // (they define what "should happen"); the vendor sees it read-only
  // and acknowledges it.
  getPatrolPlans(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolServicePlan>>('/client/patrol-plans/', { params });
  },
  getPatrolPlan(id: string) {
    return api.get<PatrolServicePlan>(`/client/patrol-plans/${id}/`);
  },
  createPatrolPlan(data: {
    coverage_id: string;
    notes?: string;
    required_visits?: Partial<RequiredVisit>[];
  }) {
    return api.post<PatrolServicePlan>('/client/patrol-plans/', data);
  },
  updatePatrolPlan(id: string, data: {
    notes?: string;
    required_visits?: Partial<RequiredVisit>[];
  }) {
    return api.patch<PatrolServicePlan>(`/client/patrol-plans/${id}/`, data);
  },
  deletePatrolPlan(id: string) {
    return api.delete(`/client/patrol-plans/${id}/`);
  },
  // ServiceCoverage endpoints
  getServiceCoverages(params?: Record<string, string>) {
    return api.get<PaginatedResponse<ServiceCoverage>>('/client/service-coverages/', { params });
  },
  createServiceCoverage(data: {
    site_id: string;
    service_type: string;
    is_active?: boolean;
    effective_from: string;
    notes?: string;
  }) {
    return api.post<ServiceCoverage>('/client/service-coverages/', data);
  },

  // ---- Sprint 3: verifications, exceptions, change requests ----
  getVisitVerifications(params: Record<string, string>) {
    return api.get<PaginatedResponse<VisitVerification>>(
      '/client/visit-verifications/', { params },
    );
  },
  getPatrolExceptions(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolException>>(
      '/client/patrol-exceptions/', { params },
    );
  },
  createPatrolException(data: {
    site_id: string;
    required_visit_id?: string | null;
    exception_type: string;
    label: string;
    reason?: string;
    starts_on: string;
    ends_on: string;
  }) {
    return api.post<PatrolException>('/client/patrol-exceptions/', data);
  },
  deletePatrolException(id: string) {
    return api.delete(`/client/patrol-exceptions/${id}/`);
  },
  getPatrolChangeRequests(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolPlanChangeRequest>>(
      '/client/patrol-change-requests/', { params },
    );
  },
  decidePatrolChangeRequest(id: string, decision: 'accepted' | 'rejected', notes?: string) {
    return api.post<PatrolPlanChangeRequest>(
      `/client/patrol-change-requests/${id}/decide/`,
      { decision, notes: notes ?? '' },
    );
  },

  // Find (or create) the active mobile_patrol ServiceCoverage for a
  // site.  Called from the site detail page when the client wants to
  // build a patrol plan.
  async ensureMobilePatrolCoverage(siteId: string): Promise<ServiceCoverage> {
    const r = await api.get<PaginatedResponse<ServiceCoverage>>(
      '/client/service-coverages/',
      { params: { site: siteId, service_type: 'mobile_patrol', is_active: 'true' } },
    );
    const existing = r.data.results?.[0];
    if (existing) return existing;
    const created = await api.post<ServiceCoverage>('/client/service-coverages/', {
      site_id: siteId,
      service_type: 'mobile_patrol',
      is_active: true,
      effective_from: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    return created.data;
  },
};
